import React, { useState, useEffect } from 'react';

/**
 * VIN Validation Component
 * - Enforces exactly 17 characters
 * - Blocks invalid characters (I, O, Q)
 * - Validates check digit (position 9)
 * - Shows real-time character count and validation status
 */

// VIN check digit calculation weights and transliteration
const TRANSLITERATION = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
    0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9
};

const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

const calculateCheckDigit = (vin) => {
    if (vin.length !== 17) return null;

    let sum = 0;
    for (let i = 0; i < 17; i++) {
        const char = vin[i].toUpperCase();
        const value = TRANSLITERATION[char];
        if (value === undefined) return null;
        sum += value * WEIGHTS[i];
    }

    const remainder = sum % 11;
    return remainder === 10 ? 'X' : String(remainder);
};

const isValidVinCharacter = (char) => {
    // VINs don't contain I, O, or Q
    return /^[A-HJ-NPR-Z0-9]$/i.test(char);
};

const VinInput = ({
    value,
    onChange,
    required = false,
    disabled = false,
    className = '',
    placeholder = 'Enter 17-character VIN'
}) => {
    const [localValue, setLocalValue] = useState(value || '');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setLocalValue(value || '');
    }, [value]);

    const handleChange = (e) => {
        let newValue = e.target.value.toUpperCase();

        // Filter out invalid characters (I, O, Q)
        newValue = newValue.split('').filter(isValidVinCharacter).join('');

        // Limit to 17 characters
        if (newValue.length > 17) {
            newValue = newValue.slice(0, 17);
        }

        setLocalValue(newValue);

        // Call parent onChange with synthetic event
        if (onChange) {
            onChange({
                target: {
                    name: 'vin',
                    value: newValue
                }
            });
        }
    };

    const charCount = localValue.length;
    const isComplete = charCount === 17;

    // Format-only validation (17 valid chars). Check digit validation
    // is skipped because it's a North American standard — vehicles in
    // other markets (Africa, Europe, Asia) often don't follow it.
    const isValid = isComplete;
    const hasError = false;

    // Status colour + message — chrome/ink signals, French.
    let statusColor = 'text-[#5C554C]';
    let statusMessage = '';

    if (charCount === 0) {
        statusMessage = '0/17';
    } else if (!isComplete) {
        statusColor = 'text-[#DFA23C]';
        statusMessage = `${charCount}/17 · ${17 - charCount} restants`;
    } else {
        statusColor = 'text-[#74A98D]';
        statusMessage = '17/17 ✓';
    }

    // Border colour based on state
    let borderColor = 'border-[#2B2724]';
    if (isValid) {
        borderColor = 'border-[#74A98D]/50';
    } else if (hasError) {
        borderColor = 'border-[#E2574B]/60';
    } else if (isFocused) {
        borderColor = 'border-[#4E4740]';
    }

    return (
        <div className={`vin-input-container ${className}`}>
            <div className="relative">
                <input
                    type="text"
                    name="vin"
                    value={localValue}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    maxLength={17}
                    className={`w-full h-[38px] pl-3 pr-[84px] rounded-[9px] border bg-[#100F0D] text-[#F4F1EA]
                                font-['Space_Grotesk',sans-serif] text-[13px] uppercase outline-none transition-colors
                                placeholder:text-[#5C554C] placeholder:normal-case ${borderColor}
                                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ letterSpacing: '0.1em' }}
                    autoComplete="off"
                    spellCheck="false"
                />

                {/* Character count badge */}
                <div className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px] font-medium tabular-nums pointer-events-none ${statusColor}`}>
                    {statusMessage}
                </div>
            </div>

            {/* Helper text */}
            {isFocused && charCount < 17 && (
                <p className="mt-1 text-[10.5px] text-[#5C554C]">
                    Le VIN fait exactement 17 caractères. Les lettres I, O et Q ne sont pas autorisées.
                </p>
            )}

            {/* Check digit validation removed — not applicable outside North America */}
        </div>
    );
};

// Utility function to validate VIN (can be used externally)
export const validateVin = (vin) => {
    if (!vin || vin.length !== 17) return false;

    // Check for invalid characters
    for (const char of vin) {
        if (!isValidVinCharacter(char)) return false;
    }

    // Validate check digit
    const expectedCheck = calculateCheckDigit(vin.toUpperCase());
    return expectedCheck === vin[8].toUpperCase();
};

// Utility to check if VIN has valid format (length and characters) even if check digit might be wrong
export const isVinFormatValid = (vin) => {
    if (!vin || vin.length !== 17) return false;
    for (const char of vin) {
        if (!isValidVinCharacter(char)) return false;
    }
    return true;
};

export default VinInput;
